/**
 * Universal file download utility.
 *
 * v1.3.26.1 — Order-of-operations rewrite. The prior version prompted
 * the OS "Save As" dialog FIRST and fetched the file AFTER. Two ways
 * that used to fail silently:
 *   (a) Backend returned 200 with an empty body → we still wrote an
 *       empty blob to the user's chosen file → 0 KB PDF on disk that
 *       Adobe couldn't open, no error toast.
 *   (b) Backend returned 500 → we threw and fell through to the
 *       fallback path, which re-fetched (double load) and threw again.
 *
 * New order: FETCH first, validate response body has bytes, THEN
 * prompt the picker. If anything is wrong with the response, throw
 * before the picker opens so no phantom file is created and the
 * caller's catch block shows a real error toast.
 */
import { API_BASE } from './config';

const MIN_BYTES = 100; // real PDFs are ~1 KB minimum; guard against silent-empty responses

/**
 * @param {string} path       — API path WITHOUT /api/ prefix
 *                               e.g. "/dashboard/export-pdf" or "/docs/system-overview/pdf"
 * @param {string} authToken  — JWT token
 * @param {string} filename   — Suggested filename e.g. "Dashboard_Report.pdf"
 * @param {object} [options]  — Optional: { method: 'POST', body: {...} }
 */
export async function downloadFile(path, authToken, filename = 'download.pdf', options = {}) {
  const url = `${API_BASE}${path}`;
  const fetchOpts = {
    method: options.method || 'GET',
    headers: { 'Authorization': `Bearer ${authToken}` },
  };
  if (options.body) {
    fetchOpts.headers['Content-Type'] = 'application/json';
    fetchOpts.body = JSON.stringify(options.body);
  }

  // ── STEP 1: Fetch the file bytes FIRST and verify they're real. ─────────
  // Any failure here throws before we ever touch the filesystem.
  const res = await fetch(url, fetchOpts);
  if (!res.ok) {
    // Try to pull a helpful detail message from the API's JSON error.
    let detail = '';
    try {
      const errJson = await res.clone().json();
      detail = errJson?.detail || errJson?.message || '';
    } catch { /* body wasn't JSON; ignore */ }
    throw new Error(detail ? `Download failed (${res.status}): ${detail}` : `Download failed: ${res.status}`);
  }
  const blob = await res.blob();
  if (!blob || blob.size < MIN_BYTES) {
    throw new Error(`Download returned an empty file (${blob?.size || 0} bytes). The server likely could not generate this document — please save the report and try again.`);
  }

  // Determine file types for the save dialog
  const ext = filename.split('.').pop().toLowerCase();
  const mimeTypes = {
    pdf: { 'application/pdf': ['.pdf'] },
    docx: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] },
    csv: { 'text/csv': ['.csv'] },
    txt: { 'text/plain': ['.txt'] },
    xlsx: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
  };
  const accept = mimeTypes[ext] || { 'application/octet-stream': [`.${ext}`] };

  // ── STEP 2: Primary — File System Access API (Chrome / Edge). ────────────
  // Only prompt AFTER we have valid bytes in hand, so the picker never
  // leaves an empty file behind on failure.
  if (window.showSaveFilePicker) {
    try {
      const fileHandle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: filename, accept }],
      });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (err) {
      if (err.name === 'AbortError') return; // user cancelled — don't fall through
      console.warn('showSaveFilePicker failed, trying blob-anchor fallback:', err.message);
    }
  }

  // ── STEP 3: Fallback — blob URL + <a download>. ──────────────────────────
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}
